import { createContext, useMemo, useContext, useState, useEffect, useLayoutEffect } from 'react';
import { useRef, useCallback } from 'react';

const GeneralContext = createContext();

export function GeneralProvider({ token, setToken, removeToken, ...props }) {
    const tokenR = useRef(token);
    // Sincronizar tokenR en cada render para que los efectos hijos vean el valor correcto
    tokenR.current = token;

    const [infoUser, setInfoUser] = useState([]);
    const [isConsulta, setIsConsulta] = useState(false);
    const [sessionExpired, setSessionExpired] = useState(false);
    const isLoggingOutRef = useRef(false);
    const originalFetchRef = useRef(null);
    const fetchPatchedRef = useRef(false);

    // Resetear el guard de logout al recibir un token nuevo (re-login)
    useEffect(() => {
        if (token) {
            isLoggingOutRef.current = false;
            setSessionExpired(false);
        }
    }, [token]);

    // logout estable: removeToken es estable (useCallback [] en useToken.js)
    const logout = useCallback((expired = false) => {
        if (isLoggingOutRef.current) return;
        isLoggingOutRef.current = true;
        if (expired) setSessionExpired(true);
        removeToken();
    }, [removeToken]);

    const renovarToken = useCallback(async () => {
        if (!tokenR.current) return;
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/usuarios/renovartoken`,
                {
                    method: 'GET',
                    headers: {
                        authorization: `Bearer ${tokenR.current}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                }
            );
            if (res.ok) {
                const respuesta = await res.json();
                setToken(respuesta);
                tokenR.current = respuesta.token;
            }
        } catch { /* ignore */ }
    }, [setToken]);

    // Renovación periódica cada 10 min
    useEffect(() => {
        const interval = setInterval(renovarToken, 600000);
        return () => clearInterval(interval);
    }, [renovarToken]);

    // Renovar al volver al tab (si estuvo oculto durante el intervalo)
    useEffect(() => {
        const onVisible = () => {
            if (!document.hidden) renovarToken();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [renovarToken]);

    // Renovar proactivamente al montar con token existente (remember me / reload)
    useEffect(() => {
        if (tokenR.current) renovarToken();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Interceptor de fetch: llama logout en cualquier 401.
    // useLayoutEffect garantiza que se instala ANTES de que cualquier useEffect
    // hijo (HomeContext, MonitoreoContext, etc.) haga sus llamadas iniciales.
    useLayoutEffect(() => {
        if (typeof window === 'undefined' || fetchPatchedRef.current) {
            return;
        }
        originalFetchRef.current = window.fetch.bind(window);
        window.fetch = async (...args) => {
            const response = await originalFetchRef.current(...args);
            if (response?.status === 401 && tokenR.current) {
                logout(true);
            }
            return response;
        };
        fetchPatchedRef.current = true;
        return () => {
            if (fetchPatchedRef.current && originalFetchRef.current) {
                window.fetch = originalFetchRef.current;
                fetchPatchedRef.current = false;
            }
        };
    }, [logout]);

    const generalValues = useMemo(() => {
        return {
            infoUser,
            setInfoUser,
            isConsulta,
            setIsConsulta,
            tokenR,
            logout,
            sessionExpired,
            setSessionExpired,
        };
    }, [infoUser, isConsulta, logout, sessionExpired]);

    return <GeneralContext.Provider value={generalValues} {...props} />;
}

export function useGeneral() {
    const context = useContext(GeneralContext);
    if (!context) {
        throw new Error('useGeneral debe estar dentro del proveedor GeneralContext');
    }
    return context;
}
